import { makeObservable, observable } from 'mobx';

export class StringHashParameterStore {
  @observable.ref private value: string | undefined;

  constructor(private readonly key: string) {
    this.value = this.getFromUrl();
    makeObservable(this);
    window.addEventListener('hashchange', () => {
      this.value = this.getFromUrl();
    });
  }

  private getFromUrl() {
    const hashParams = new URLSearchParams(window.location.hash.slice(1));
    return hashParams.get(this.key) || undefined;
  }

  readonly set = (value: string | undefined) => {
    this.value = value;
    const hashParams = new URLSearchParams(window.location.hash.slice(1));
    if (value != null) {
      hashParams.set(this.key, value);
    } else {
      hashParams.delete(this.key);
    }
    window.location.hash = hashParams.toString();
  };

  get() {
    return this.value;
  }
}

export class EnumHashParameterStore<T extends string> {
  private readonly store = new StringHashParameterStore(this.key);

  constructor(
    private readonly key: string,
    private readonly values: readonly T[],
    private readonly defaultValue: T,
  ) {}

  readonly set = (value: T | undefined) => {
    this.store.set(value);
  };

  get() {
    const raw = this.store.get();
    return this.values.indexOf(raw as T) >= 0 ? (raw as T) : this.defaultValue;
  }
}

// Base62 characters for URL-safe encoding
const BASE62 = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';

function base62Encode(num: bigint): string {
  if (num === BigInt(0)) return '0';
  let result = '';
  while (num > BigInt(0)) {
    result = BASE62[Number(num % BigInt(62))] + result;
    num = num / BigInt(62);
  }
  return result;
}

function base62Decode(str: string): bigint {
  let result = BigInt(0);
  for (let i = 0; i < str.length; i++) {
    const char = str[i];
    const value = BigInt(BASE62.indexOf(char));
    result = result * BigInt(62) + value;
  }
  return result;
}

function uuidToBase62(uuid: string): string {
  // Remove dashes and convert to base62
  const hex = uuid.replace(/-/g, '');
  const num = BigInt('0x' + hex);
  return base62Encode(num);
}

function base62ToUuid(base62: string): string {
  // Convert base62 back to hex and format as UUID
  const num = base62Decode(base62);
  const hex = num.toString(16).padStart(32, '0');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(
    12,
    16,
  )}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export class UuidHashParameterStore {
  private readonly store = new StringHashParameterStore(this.key);

  constructor(private readonly key: string) {}

  readonly set = (value: string | undefined) => {
    if (value) {
      // Validate UUID format, if invalid just set to undefined
      if (
        !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
          value,
        )
      ) {
        this.store.set(undefined);
        return;
      }
      this.store.set(uuidToBase62(value));
    } else {
      this.store.set(undefined);
    }
  };

  get() {
    const short = this.store.get();
    if (!short) return undefined;
    try {
      return base62ToUuid(short);
    } catch {
      // If there's any error in decoding (e.g. invalid base62 string), return undefined
      return undefined;
    }
  }
}
